import PropTypes from 'prop-types';
import { useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AdminActionBar from './AdminActionBar';
import AdminConfirmationDialog from './AdminConfirmationDialog';
import AdminDataTable from './AdminDataTable';
import AdminDetailPanel from './AdminDetailPanel';
import AdminErrorState from './AdminErrorState';
import AdminFilterBar from './AdminFilterBar';
import AdminFormSection from './AdminFormSection';
import AdminLoadingState from './AdminLoadingState';
import AdminNoDataState from './AdminNoDataState';
import AdminStatusChip from './AdminStatusChip';
import AdminTimeline from './AdminTimeline';

export default function AdminModulePlaceholder({ module }) {
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} xl={8}>
          <Card
            sx={{
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.grey[900]} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'common.white',
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <AdminStatusChip status={module.mode === 'refactored' ? 'active' : 'pending'} />
                  {module.mode === 'refactored' ? (
                    <AdminStatusChip status={module.contractStatus || 'placeholder_ready'} />
                  ) : (
                    <AdminStatusChip status="active" />
                  )}
                  {module.statuses.slice(0, 2).map((status) => (
                    <AdminStatusChip key={status} status={status} />
                  ))}
                </Stack>
                <Box>
                  <Typography variant="h3" sx={{ mb: 1 }}>
                    {module.title}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.78)', maxWidth: 760 }}>
                    {module.description}
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.64)' }}>
                      Backend coverage
                    </Typography>
                    <Typography variant="h4">{module.routeGroups.length}</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                      documented routes represented in this shell
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.64)' }}>
                      State handling
                    </Typography>
                    <Typography variant="h4">{module.statuses.length}</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                      distinct statuses preserved from the contract
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.64)' }}>
                      Delivery mode
                    </Typography>
                    <Typography variant="h4">{module.mode === 'refactored' ? 'Refactored' : 'Legacy'}</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                      {module.mode === 'refactored'
                        ? 'Target routes for new implementation'
                        : 'Still active and intentionally accessible'}
                    </Typography>
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} xl={4}>
          <AdminDetailPanel title="Module Contract Summary" items={module.detailItems} />
        </Grid>

        <Grid item xs={12}>
          <AdminActionBar
            title="Action bar pattern"
            description="This shared action bar is ready for risky, blocked, and recoverable admin operations once backend wiring begins."
            actions={[
              { label: 'Refresh shell', icon: 'solar:refresh-bold', variant: 'contained' },
              { label: 'Open confirmation', icon: 'solar:danger-triangle-bold', color: 'error' },
              { label: 'Disabled by permissions', icon: 'solar:lock-bold', variant: 'outlined' },
            ]}
            onActionClick={(action) => {
              if (action.label === 'Open confirmation') {
                setConfirmationOpen(true);
              }
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <AdminFilterBar
            title="Filter bar pattern"
            hint="Shared filters are structured for operational search, status slicing, and future server-driven filtering."
            filters={module.statuses}
          />
        </Grid>

        <Grid item xs={12} lg={8}>
          <AdminDataTable
            title="Module table pattern"
            description="Reusable data-table surface for dense operational records and status-heavy lists."
            columns={module.sampleColumns}
            rows={module.sampleRows}
            emptyTitle="No records connected yet"
            emptyDescription="This table is intentionally empty until this module is wired to its documented backend routes."
          />
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            <AdminTimeline title="Readiness timeline" items={module.timeline} />
            <AdminLoadingState rows={3} />
          </Stack>
        </Grid>

        <Grid item xs={12} lg={7}>
          <AdminFormSection
            title="Form section pattern"
            description="Shared form-section wrapper for controlled updates, review notes, and approval or rejection actions."
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="Reference field" placeholder="Example: ADM1002" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth size="small" label="Action notes" placeholder="Add review or audit notes" />
              </Grid>
            </Grid>
            <Alert severity="info" variant="outlined">
              Form fields are intentionally static until this screen is connected to a documented payload contract.
            </Alert>
          </AdminFormSection>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Stack spacing={3}>
            <AdminErrorState
              title="Error state pattern"
              message="Use this block when a documented backend route fails or returns a permission or workflow issue."
            />
            <AdminNoDataState
              title="No-data pattern"
              description="Use this state when the route is active and reachable but currently has no records to present."
            />
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="subtitle1">Documented route coverage</Typography>
                <Typography variant="body2" color="text.secondary">
                  This shell only references route groups documented as active in the backend contract.
                </Typography>
                <Grid container spacing={1.5}>
                  {module.routeGroups.map((route) => (
                    <Grid item xs={12} md={6} key={route}>
                      <Box
                        sx={{
                          px: 2,
                          py: 1.5,
                          borderRadius: 2,
                          bgcolor: 'grey.100',
                          border: (theme) => `1px solid ${theme.palette.divider}`,
                          fontFamily: 'monospace',
                          fontSize: 13,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {route}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={1.25}>
                <Typography variant="subtitle1">Implementation notes</Typography>
                {module.bullets.map((bullet) => (
                  <Typography key={bullet} variant="body2" color="text.secondary">
                    {bullet}
                  </Typography>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <AdminConfirmationDialog
        open={confirmationOpen}
        title="Confirmation pattern"
        description="Use this shared confirmation dialog for destructive or risky admin actions such as revoke, cancel, release, or remove block."
        confirmLabel="Acknowledge"
        onClose={() => setConfirmationOpen(false)}
        onConfirm={() => setConfirmationOpen(false)}
      />
    </>
  );
}

AdminModulePlaceholder.propTypes = {
  module: PropTypes.shape({
    bullets: PropTypes.arrayOf(PropTypes.string).isRequired,
    contractStatus: PropTypes.string,
    description: PropTypes.string.isRequired,
    detailItems: PropTypes.arrayOf(PropTypes.object).isRequired,
    mode: PropTypes.oneOf(['refactored', 'legacy']).isRequired,
    routeGroups: PropTypes.arrayOf(PropTypes.string).isRequired,
    sampleColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
    sampleRows: PropTypes.arrayOf(PropTypes.object),
    statuses: PropTypes.arrayOf(PropTypes.string).isRequired,
    timeline: PropTypes.arrayOf(PropTypes.object).isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
};
